import { useMemo } from 'react'
import { Tooltip } from '@cloudflare/kumo'
import type {
  MatrixApiResponse,
  MatrixCell,
  Health,
  PortalAccount,
  PortalServer,
} from '../types'
import { healthStyle } from '../health'
import type { Filters } from './FilterBar'

const HEALTH_RANK: Record<Health, number> = {
  blocked: 0,
  error: 1,
  degraded: 2,
  no_data: 3,
  ok: 4,
}

function rollup(healths: Health[]): Health {
  if (healths.length === 0) return 'no_data'
  if (healths.includes('blocked')) return 'blocked'
  if (healths.includes('error')) return 'error'
  if (healths.includes('degraded')) return 'degraded'
  if (healths.includes('ok')) return 'ok'
  return 'no_data'
}

/** Does a cell's health satisfy the active status filter? */
function matchesStatus(health: Health, status: Filters['status']): boolean {
  switch (status) {
    case 'all':
      return true
    case 'broken':
      return health === 'blocked' || health === 'error' || health === 'degraded'
    default:
      return health === status
  }
}

export interface MatrixSelection {
  accountId: string
  serverId: string
}

interface Props {
  data: MatrixApiResponse
  filters: Filters
  onSelectServer: (id: string) => void
  onSelectAccount: (id: string) => void
  onSelectCell: (sel: MatrixSelection) => void
  activeCell?: MatrixSelection | null
}

export function HealthGrid({
  data,
  filters,
  onSelectServer,
  onSelectAccount,
  onSelectCell,
  activeCell,
}: Props) {
  const { accounts, servers, cells } = data
  const q = filters.query.trim().toLowerCase()

  // Cells that pass the status + query predicates.
  const visibleCells = useMemo(() => {
    return cells.filter((c) => {
      if (!matchesStatus(c.health, filters.status)) return false
      if (q) {
        const hit =
          c.account_name.toLowerCase().includes(q) ||
          c.account_id.toLowerCase().includes(q) ||
          c.server_name.toLowerCase().includes(q) ||
          c.provider.toLowerCase().includes(q)
        if (!hit) return false
      }
      return true
    })
  }, [cells, filters.status, q])

  const cellMap = useMemo(() => {
    const m = new Map<string, MatrixCell>()
    for (const c of visibleCells) m.set(`${c.account_id}|${c.server_id}`, c)
    return m
  }, [visibleCells])

  // Per-axis rolled-up health (from all cells, for stable header coloring).
  const serverHealth = useMemo(() => {
    const m = new Map<string, Health>()
    for (const s of servers) {
      m.set(
        s.server_id,
        rollup(cells.filter((c) => c.server_id === s.server_id).map((c) => c.health))
      )
    }
    return m
  }, [servers, cells])

  const accountHealth = useMemo(() => {
    const m = new Map<string, Health>()
    for (const a of accounts) {
      m.set(
        a.account_id,
        rollup(cells.filter((c) => c.account_id === a.account_id).map((c) => c.health))
      )
    }
    return m
  }, [accounts, cells])

  // Which servers/accounts have at least one visible cell + pass axis filters.
  const visibleServers: PortalServer[] = useMemo(() => {
    const withCells = new Set(visibleCells.map((c) => c.server_id))
    return [...servers]
      .filter((s) => filters.serverId === 'all' || s.server_id === filters.serverId)
      .filter((s) => withCells.has(s.server_id))
      .sort(
        (a, b) =>
          HEALTH_RANK[serverHealth.get(a.server_id) ?? 'no_data'] -
          HEALTH_RANK[serverHealth.get(b.server_id) ?? 'no_data']
      )
  }, [servers, visibleCells, filters.serverId, serverHealth])

  const visibleAccounts: PortalAccount[] = useMemo(() => {
    const withCells = new Set(visibleCells.map((c) => c.account_id))
    return [...accounts]
      .filter((a) => filters.accountId === 'all' || a.account_id === filters.accountId)
      .filter((a) => withCells.has(a.account_id))
      .sort(
        (a, b) =>
          HEALTH_RANK[accountHealth.get(a.account_id) ?? 'no_data'] -
          HEALTH_RANK[accountHealth.get(b.account_id) ?? 'no_data']
      )
  }, [accounts, visibleCells, filters.accountId, accountHealth])

  if (visibleServers.length === 0 || visibleAccounts.length === 0) {
    return (
      <div className="rounded-xl border border-kumo-hairline bg-kumo-base py-16 text-center">
        <span className="text-kumo-subtle">No connections match the current filters.</span>
      </div>
    )
  }

  return (
    <div className="overflow-auto rounded-xl border border-kumo-hairline bg-kumo-base">
      <table className="border-separate border-spacing-0 w-full">
        <thead>
          <tr>
            <th className="sticky left-0 top-0 z-30 bg-kumo-base border-b border-r border-kumo-hairline px-3.5 py-2.5 text-left min-w-[200px]">
              <span className="text-xs uppercase tracking-wide text-kumo-subtle">
                Account \ Server
              </span>
            </th>
            {visibleServers.map((s) => {
              const h = serverHealth.get(s.server_id) ?? 'no_data'
              const st = healthStyle(h)
              return (
                <th
                  key={s.server_id}
                  onClick={() => onSelectServer(s.server_id)}
                  title={`${s.server_name} (${s.provider}) — click for server view`}
                  className="sticky top-0 z-20 bg-kumo-base border-b border-kumo-hairline px-2 py-2.5 cursor-pointer min-w-[92px] align-bottom hover:bg-kumo-tint"
                >
                  <div className="flex flex-col items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${st.dotBg}`} />
                    <span
                      className="text-xs font-semibold text-kumo-default max-h-[110px] overflow-hidden text-ellipsis whitespace-nowrap"
                      style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
                    >
                      {s.server_name}
                    </span>
                  </div>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {visibleAccounts.map((a) => {
            const rowH = accountHealth.get(a.account_id) ?? 'no_data'
            const rowSt = healthStyle(rowH)
            return (
              <tr key={a.account_id}>
                <th
                  onClick={() => onSelectAccount(a.account_id)}
                  title={`${a.account_name} — click for customer view`}
                  className="sticky left-0 z-10 bg-kumo-base border-r border-b border-kumo-hairline px-3.5 py-2 cursor-pointer text-left hover:bg-kumo-tint"
                >
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${rowSt.dotBg}`} />
                    <span className="min-w-0">
                      <div className="text-sm font-semibold text-kumo-default whitespace-nowrap">
                        {a.account_name}
                      </div>
                      <div className="text-[10px] text-kumo-subtle font-mono">
                        {a.account_id.slice(0, 12)}…
                      </div>
                    </span>
                  </div>
                </th>
                {visibleServers.map((s) => {
                  const cell = cellMap.get(`${a.account_id}|${s.server_id}`)
                  const h: Health = cell?.health ?? 'no_data'
                  const st = healthStyle(h)
                  const isActive =
                    activeCell?.accountId === a.account_id &&
                    activeCell?.serverId === s.server_id
                  return (
                    <td
                      key={s.server_id}
                      className="border-b border-kumo-hairline p-1.5 text-center"
                    >
                      {cell ? (
                        <Tooltip
                          content={`${a.account_name} → ${s.server_name}: ${st.label}`}
                        >
                          <button
                            onClick={() =>
                              onSelectCell({
                                accountId: a.account_id,
                                serverId: s.server_id,
                              })
                            }
                            className={`block w-[30px] h-[22px] mx-auto rounded-md cursor-pointer ${st.cell} ${
                              isActive ? 'outline outline-2 outline-kumo-brand outline-offset-1' : ''
                            }`}
                            aria-label={`${a.account_name} to ${s.server_name}: ${st.label}`}
                          />
                        </Tooltip>
                      ) : (
                        <div className="w-[30px] h-[22px] mx-auto rounded-md border border-dashed border-kumo-hairline" />
                      )}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
